import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    IconButton,
    Typography,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
} from '@mui/icons-material';
import moment from 'moment';

const FollowUpWeekPicker = ({
    edit,
    updateTreatment,
    setUpdateTreatment,
}) => {
    const today = moment().startOf('day');

    const [startDate, setStartDate] = useState(() => {
        if (updateTreatment?.followUpTestDate) {
            const selected = moment(
                updateTreatment.followUpTestDate
            ).startOf('day');

            if (selected.isSameOrAfter(today, 'day')) {
                return selected;
            }
        }

        return today;
    });

    const [touchStart, setTouchStart] = useState(null);

    const selectedDate = updateTreatment?.followUpTestDate
        ? moment(updateTreatment.followUpTestDate).format('YYYY-MM-DD')
        : '';
    console.log("ss", selectedDate)

    useEffect(() => {
        if (!updateTreatment?.followUpTestDate) {
            setStartDate(today);
            return;
        }

        const selected = moment(
            updateTreatment.followUpTestDate
        ).startOf('day');

        // If selected date is in the past,
        // start from today.
        if (selected.isBefore(today, 'day')) {
            setStartDate(today);
            return;
        }

        // If selected date is already visible,
        // don't change the current week.
        setStartDate(prev => {
            const endDate = moment(prev).add(6, 'days');

            const isVisible =
                selected.isSameOrAfter(prev, 'day') &&
                selected.isSameOrBefore(endDate, 'day');

            return isVisible ? prev : selected;
        });
    }, [updateTreatment?.followUpTestDate]);

    const dates = useMemo(() => {
        return Array.from({ length: 7 }, (_, index) =>
            moment(startDate).add(index, 'days')
        );
    }, [startDate]);

    const selectDate = (date) => {
        if (!edit) return;

        setUpdateTreatment(prev => ({
            ...prev,
            followUpTestDate: date.toISOString(),
        }));
    };

    const nextWeek = () => {
        if (!edit) return;

        setStartDate(prev =>
            moment(prev).add(7, 'days')
        );
    };

    const previousWeek = () => {
        if (!edit) return;

        const newStartDate = moment(startDate).subtract(7, 'days');

        if (newStartDate.isBefore(today, 'day')) {
            setStartDate(today);
        } else {
            setStartDate(newStartDate);
        }
    };

    const handleTouchStart = (e) => {
        setTouchStart(e.touches[0].clientX);
    };

    const handleTouchEnd = (e) => {
        if (touchStart === null) return;

        const touchEnd = e.changedTouches[0].clientX;
        const distance = touchStart - touchEnd;

        if (distance > 50) {
            nextWeek();
        }

        if (distance < -50) {
            previousWeek();
        }

        setTouchStart(null);
    };

    return (
        <Box
            sx={{
                mt: 2,
                width: '100%',
                userSelect: 'none',
                touchAction: 'pan-y',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                {/* LEFT ARROW */}
                <IconButton
                    onClick={previousWeek}
                    disabled={
                        !edit ||
                        startDate.isSame(today, 'day')
                    }
                    sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                    }}
                >
                    <ChevronLeft />
                </IconButton>

                {/* DATES */}
                <Box
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    sx={{
                        display: 'flex',
                        flex: 1,
                        gap: '4px',
                        overflow: 'hidden',
                    }}
                >
                    {dates.map((date) => {
                        const dateString = date.format('YYYY-MM-DD');

                        const isSelected =
                            selectedDate === dateString;

                        const isToday =
                            date.isSame(today, 'day');

                        return (
                            <Box
                                key={dateString}
                                onClick={() => selectDate(date)}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    height: 65,
                                    borderRadius: '8px',

                                    cursor: edit
                                        ? 'pointer'
                                        : 'default',

                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',

                                    backgroundColor: isSelected
                                        ? '#1976d2'
                                        : '#fff',

                                    border: isSelected
                                        ? '2px solid #1976d2'
                                            : '1px solid #E6E8EC',

                                    color: isSelected
                                        ? '#fff'
                                        : '#333',

                                    transition:
                                        'all 0.2s ease',

                                    '&:hover': edit
                                        ? {
                                            backgroundColor:
                                                isSelected
                                                    ? '#1976d2'
                                                    : '#f5f7fa',
                                        }
                                        : {},
                                }}
                            >
                                {/* DAY */}
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        opacity: isSelected
                                            ? 0.9
                                            : 0.6,
                                    }}
                                >
                                    {date.format('ddd')}
                                </Typography>

                                {/* DATE */}
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {date.format('DD')}
                                </Typography>

                                {/* MONTH */}
                                <Typography
                                    sx={{
                                        fontSize: '9px',
                                        opacity: isSelected
                                            ? 0.9
                                            : 0.6,
                                    }}
                                >
                                    {date.format('MMM')}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* RIGHT ARROW */}
                <IconButton
                    onClick={nextWeek}
                    disabled={!edit}
                    sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                    }}
                >
                    <ChevronRight />
                </IconButton>
            </Box>
        </Box>
    );
};

export default FollowUpWeekPicker;